"use client";

import Image from "next/image";
import { sbBlok } from "../types/sb-types";
import { storyblokEditable } from "@storyblok/react";
import Cta from "./Cta";
import { useEffect, useState } from "react";
import {
  NODE_PARAGRAPH,
  NODE_UL,
  NODE_OL,
  NODE_LI,
  NODE_HEADING,
  render,
} from "storyblok-rich-text-react-renderer";
import clsx from "clsx";
import HeaderLeaderboard from "./HeaderLeaderboard";
import { Tournament } from "@/types/tournament";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { GOLF_PORTAL_API } from "@/utils/constants";
import { TournamentBase } from "@/queries/tournaments";

interface HeaderProps {
  blok: sbBlok;
  tournaments: Tournament[];
}

export default function Header({ blok, tournaments }: HeaderProps) {
  const {
    description_button,
    description_button_variant,
    image,
    mobile_image,
    leaderboard_title,
    leaderboard_btn_title,
    leaderboard_btn_url,
    leaderboard_logo,
    leaderboard_tournament_id,
    logo,
    title,
  } = blok;

    const { data, isLoading } = useQuery({
    queryKey: ["tournament_base", leaderboard_tournament_id],
    queryFn: () => {
      return axios.post(GOLF_PORTAL_API, {
        query: TournamentBase,
        variables: {
          tournamentId: leaderboard_tournament_id,
        },
      });
    },
    enabled: !!leaderboard_tournament_id,
    placeholderData: (prev: any) => prev,
  });

  const [isFirstRender, setIsFirstRender] = useState(true);
  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (isFirstRender || isLoading)
    return (
      <div className="grid">
        <div className="col-start-1">
          <div
            className={`w-full h-[475px] lg:h-[550px] 2xl:h-[700px]
            bg-tm-c-gray-900 animate-pulse
          `}
          />
        </div>
      </div>
    );

  return (
    <div className="grid" {...storyblokEditable(blok)}>
      <div className="row-start-1 col-start-1">
        <div
          className={`relative h-[475px] lg:h-[550px] 2xl:h-[700px]
            after:content-[''] after:absolute after:w-full after:h-full after:bg-[linear-gradient(0deg,rgba(0,0,0,0.7)_0%,rgba(0,0,0,0)_50%)] 
            lg:after:bg-[linear-gradient(90deg,rgba(0,0,0,0.7)_0%,rgba(0,0,0,0)_65.68%)]
          `}
        >
          {mobile_image?.filename && (
            <Image
              src={mobile_image?.filename}
              alt={mobile_image?.alt}
              fill
              sizes="100vw"
              className="md:hidden object-cover"
            />
          )}
          {image?.filename && (
            <Image
              src={image.filename}
              alt={image.alt}
              fill
              sizes="100vw"
              className={clsx("object-cover", {
                "hidden md:block": mobile_image?.filename,
                block: !mobile_image?.filename,
              })}
              quality={100}
            />
          )}
        </div>
      </div>
      <div
        className={clsx(
          "grid-container row-start-1 col-start-1 lg:w-full z-[1]",
          {
            "mt-auto mx-5 mb-5 lg:m-auto": !leaderboard_title,
          }
        )}
      >
        <div className="col-start-1 col-span-4 lg:col-start-2 lg:col-span-14 2xl:col-start-4 2xl:col-span-26 my-auto mx-0">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col text-center py-10 px-0 md:pt-[150px] md:pb-[60px] md:gap-2.5 lg:text-left lg:max-w-[500px] lg:p-0 lg:gap-0 2xl:max-w-[600px]">
              {logo?.filename && (
                <div className="relative w-[97px] h-[97px] m-auto lg:m-0 lg:w-[110px] lg:h-[110px]">
                  <Image
                    src={logo.filename}
                    alt={logo.alt}
                    sizes="100vw"
                    fill
                    className="object-cover rounded-full"
                  />
                </div>
              )}
              {title?.content?.length > 0 && (
                <div>
                  {render(title, {
                    nodeResolvers: {
                      [NODE_PARAGRAPH]: (children) => (
                        <p
                          className={clsx(
                            "mb-5 text-[15px] tracking-[0.45px] lg:mb-[30px] 2xl:text-xl",
                            {
                              "mb-0": !description_button?.length,
                            }
                          )}
                        >
                          {children}
                        </p>
                      ),
                      [NODE_UL]: (children) => (
                        <ul className="pl-16 list-disc">{children}</ul>
                      ),
                      [NODE_OL]: (children) => (
                        <ol className="pl-16 list-decimal">{children}</ol>
                      ),
                      [NODE_LI]: (children) => (
                        <li className="mb-6 last:mb-0">{children}</li>
                      ),
                      [NODE_HEADING]: (
                        children,
                        props: { level: 1 | 2 | 3 | 4 | 5 | 6 }
                      ) => {
                        if (props?.level === 1) return <h1>{children}</h1>;

                        if (props?.level === 2) return <h2>{children}</h2>;

                        return <span>{children}</span>;
                      },
                    },
                  })}
                </div>
              )}
              {description_button?.length > 0 && (
                <Cta
                  href={description_button[0]?.link?.url}
                  fullSlug={description_button[0]?.link?.story?.full_slug}
                  name={description_button[0]?.name}
                  variant={description_button_variant}
                  className="self-center lg:self-auto"
                />
              )}
            </div>
            {leaderboard_title && (
              <HeaderLeaderboard
                title={leaderboard_title}
                btnTitle={leaderboard_btn_title}
                btnUrl={leaderboard_btn_url}
                logo={leaderboard_logo}
                defaultTournamentId={leaderboard_tournament_id}
                tournaments={tournaments}
                tournamentBaseData={data?.data?.data}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
